import React from "react";

/** Compute viewport coords for a popover anchored beneath a trigger element.
 *  Used together with `createPortal(..., document.body)` and `position: fixed`
 *  on the popover so it overlays everything regardless of parent overflow /
 *  z-index stacking and doesn't push surrounding layout. Re-measures on
 *  scroll/resize while `open` is true, throttled to one measurement per
 *  animation frame so a long scroll doesn't trigger 60+ React renders/sec. */
export function useFloatingCoords(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  /** The floating element itself. When provided, the popover flips ABOVE the
   *  trigger if it wouldn't fit below the viewport, and shifts LEFT if it would
   *  overflow the right edge — so a trigger near the bottom/right of the page
   *  isn't clipped off-screen. */
  floatingRef?: React.RefObject<HTMLElement | null>,
  /** Set false to ALWAYS open below the trigger (caller caps its own height
   *  to the space underneath instead of flipping above). */
  opts?: { flipAbove?: boolean },
): { top: number; left: number } | null {
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setCoords(null);
      return;
    }
    let frame = 0;
    const GAP = 4;
    const measure = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const box = floatingRef?.current?.getBoundingClientRect();
      const floatH = box?.height ?? 0;
      const floatW = box?.width ?? 0;
      // Flip above when the popover would overflow the viewport bottom but fits
      // above. floatH/floatW are 0 until the popover mounts; the scheduled second
      // pass re-measures with it present.
      const overflowsBelow = r.bottom + GAP + floatH > window.innerHeight - 8;
      const fitsAbove = r.top - GAP - floatH > 8;
      const top =
        opts?.flipAbove !== false && floatH > 0 && overflowsBelow && fitsAbove
          ? r.top - GAP - floatH
          : r.bottom + GAP;
      // Anchor to the trigger's left, but shift left so the popover never spills
      // off the right edge (a wide popover near the right of the page).
      const left =
        floatW > 0
          ? Math.max(8, Math.min(r.left, window.innerWidth - floatW - 8))
          : r.left;
      // Same-position writes still build a fresh object — skip them so
      // children (focus effect, etc.) don't re-run on every idle scroll tick.
      setCoords((prev) =>
        prev && prev.top === top && prev.left === left ? prev : { top, left },
      );
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    measure();   // first pass positions below (popover not mounted yet)
    schedule();  // second pass re-measures with the popover mounted → flips if needed
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [open, triggerRef, floatingRef, opts?.flipAbove]);
  return coords;
}
